import { useEffect, useRef } from 'react';
import type { FilterConfig } from '../types/filters';
import {
  getSavedSearchSignature,
  sanitizeSavedSearchDefinition,
  savedSearchDefinitionSchema,
  type SavedSearchDefinition,
  type SavedSearchEntityType,
} from '../types/saved-search';

const STORAGE_KEY_PREFIX = 'crm.search-session.v1.';

interface UseSearchSessionMemoryParams {
  entityType: SavedSearchEntityType;
  currentDefinition: SavedSearchDefinition;
  filterConfigs: FilterConfig[];
  onRestore: (definition: SavedSearchDefinition) => void;
  /** true quand un deep-link URL pilote déjà l'état initial (ex. ?status=, ?filters=) */
  skip?: boolean;
}

export function getSearchSessionStorageKey(entityType: SavedSearchEntityType): string {
  return `${STORAGE_KEY_PREFIX}${entityType}`;
}

/**
 * Mémorise les critères de recherche courants (texte, filtres, tri, colonnes, taille de page)
 * en sessionStorage et les réapplique à la revisite de la page — sans sauvegarde explicite,
 * indépendamment des vues sauvegardées nommées.
 */
export function useSearchSessionMemory({
  entityType,
  currentDefinition,
  filterConfigs,
  onRestore,
  skip = false,
}: UseSearchSessionMemoryParams) {
  const storageKey = getSearchSessionStorageKey(entityType);
  const restoredRef = useRef(false);
  const latestRef = useRef({ currentDefinition, filterConfigs, onRestore, skip });
  latestRef.current = { currentDefinition, filterConfigs, onRestore, skip };

  // Restauration une seule fois, au montage — déclarée avant la persistance
  // pour lire la valeur stockée avant qu'elle ne soit réécrite.
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    const latest = latestRef.current;
    if (latest.skip) return;

    let raw: string | null;
    try {
      raw = sessionStorage.getItem(storageKey);
    } catch {
      return;
    }
    if (!raw) return;

    let parsed: unknown = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // JSON corrompu : traité comme une définition invalide ci-dessous
    }
    const result = savedSearchDefinitionSchema.safeParse(parsed);
    if (!result.success) {
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        // stockage indisponible : rien à nettoyer
      }
      return;
    }

    const { definition } = sanitizeSavedSearchDefinition(result.data, latest.filterConfigs);
    if (getSavedSearchSignature(definition) === getSavedSearchSignature(latest.currentDefinition)) {
      return;
    }
    latest.onRestore(definition);
  }, [storageKey]);

  const signature = getSavedSearchSignature(currentDefinition);

  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(latestRef.current.currentDefinition));
    } catch {
      // stockage indisponible : pas de mémorisation
    }
  }, [storageKey, signature]);
}
